import {
  ConsumptionTax,
  InvoicePDFVisibilityConfig,
} from '@/contexts/dynamic-config';
import {
  EmployeeRole,
  OrderInvoiceLineItemFieldsFragment,
  OrderInvoicePartialPaymentFieldsFragment,
} from '@/types';
import { formatCurrency, roundMicrosToNearestWholeCent } from './money';
import { PaymentBatchStatus } from '@/enums';

const SUCCESSFUL_PAYMENT_BATCH_STATUSES = new Set<string>([
  PaymentBatchStatus.AUTHORISATION,
  PaymentBatchStatus.CAPTURE,
]);

/**
 * Returns the total micros a customer has successfully paid against an invoice
 * when there is still an outstanding balance, so callers can conditionally
 * render a "Partial payment" indicator without extra checks.
 *
 * @param invoice - The invoice fragment containing payment batch line items.
 * @param outstandingBalanceInMicros - The remaining balance on the invoice in micros.
 * @returns The total successfully paid amount in micros, or `null` when the
 *   invoice is fully paid or has no successful payments.
 */
export const derivePartialPaymentAmount = (
  invoice: OrderInvoicePartialPaymentFieldsFragment | null | undefined,
  outstandingBalanceInMicros: number | null,
): number | null => {
  if (!invoice?.payment_batch_line_items?.length) {
    return null;
  }

  if (!outstandingBalanceInMicros || outstandingBalanceInMicros <= 0) {
    return null;
  }

  const totalPaid = invoice.payment_batch_line_items.reduce((sum, item) => {
    const status = item.payment_batch?.status;
    if (status && SUCCESSFUL_PAYMENT_BATCH_STATUSES.has(status)) {
      return sum + item.amount_in_micros;
    }
    return sum;
  }, 0);

  return totalPaid > 0 ? totalPaid : null;
};

export const displayInvoiceTotal = (
  invoiceLineItems: OrderInvoiceLineItemFieldsFragment[],
  currencyCode: string | null,
  consumptionTax?: ConsumptionTax[] | null,
) => {
  return formatCurrency(
    invoiceLineItems.reduce((acc, lineItem) => {
      const unit_tax_in_micros =
        consumptionTax && lineItem.unit_tax_in_micros
          ? lineItem.unit_tax_in_micros
          : 0;
      return (
        acc +
        roundMicrosToNearestWholeCent(
          (lineItem.unit_price_in_micros + unit_tax_in_micros) *
            lineItem.ship_quantity,
        )
      );
    }, 0),
    currencyCode,
  );
};

/**
 * Determines if the invoice PDF button should be shown for a distributor employee.
 * By default (no config or no hidden roles), the button is visible.
 */
export const shouldShowInvoicePDFButtonForDistributor = ({
  config,
  role,
}: {
  config: InvoicePDFVisibilityConfig | null;
  role: EmployeeRole | null;
}): boolean => {
  if (!config || !config.hidden_for || !role) {
    return true;
  }

  const hiddenForDistributor = config.hidden_for.distributor;
  if (!hiddenForDistributor) {
    return true;
  }

  return !hiddenForDistributor.includes(role);
};

/**
 * Determines if the invoice PDF button should be shown for an operator employee.
 * By default (no config or no hidden roles), the button is visible.
 */
export const shouldShowInvoicePDFButtonForOperator = ({
  config,
  role,
}: {
  config: InvoicePDFVisibilityConfig | null;
  role: EmployeeRole | null;
}): boolean => {
  if (!config || !config.hidden_for || !role) {
    return true;
  }

  const hiddenForOperator = config.hidden_for.operator;
  if (!hiddenForOperator) {
    return true;
  }

  return !hiddenForOperator.includes(role);
};

/**
 * Returns true when the supplier has disabled the fallback to
 * Peper invoice-PDF and has no custom invoice PDF, otherwise, false.
 */
export const isPepperInvoicePDFHidden = ({
  disablePepperInvoicePdf,
  hasCustomPdf,
}: {
  disablePepperInvoicePdf: boolean;
  hasCustomPdf: boolean;
}): boolean => disablePepperInvoicePdf && !hasCustomPdf;

/**
 * Determines if the invoice PDF button should be shown based on user type and role.
 * This convenience function delegates to the appropriate user-type-specific function.
 * By default (no config), the button is visible.
 */
export const shouldShowInvoicePDFButton = ({
  config,
  operatorEmployeeRole,
  supplierEmployeeRole,
  disablePepperInvoicePdf,
  hasCustomPdf,
}: {
  config: InvoicePDFVisibilityConfig | null;
  disablePepperInvoicePdf: boolean;
  hasCustomPdf: boolean;
  operatorEmployeeRole: EmployeeRole | null;
  supplierEmployeeRole: EmployeeRole | null;
}): boolean => {
  if (isPepperInvoicePDFHidden({ disablePepperInvoicePdf, hasCustomPdf })) {
    return false;
  }

  if (supplierEmployeeRole) {
    return shouldShowInvoicePDFButtonForDistributor({
      config,
      role: supplierEmployeeRole,
    });
  }

  return shouldShowInvoicePDFButtonForOperator({
    config,
    role: operatorEmployeeRole,
  });
};
